/**
 * Layout de la sección Onboarding — menú lateral con el índice de
 * capítulos + secciones (Documentos, Vídeos, FAQ). Marca el activo
 * según la URL actual. Los hijos se pintan vía <Outlet />.
 *
 * Vive dentro del ThemedLayout de Refine (que ya provee el header y
 * el sider global), así que este layout sólo aporta el sub-sider del
 * manual.
 */

import { Menu } from "antd";
import {
  HomeOutlined,
  FileTextOutlined,
  VideoCameraOutlined,
  QuestionCircleOutlined,
  CloudUploadOutlined,
} from "@ant-design/icons";
import { Link, Outlet, useLocation } from "react-router";
import type { MenuProps } from "antd";
import { useGetIdentity } from "@refinedev/core";
import { chapters } from "@/data/onboarding";
import type { Chapter } from "@/data/onboarding/types";
import type { UserInfo } from "@/types/auth";
import { ADMIN_USER_TYPES } from "@/types/auth";
import { getIcon } from "./icons";
import { OnboardingAssetsProvider } from "./OnboardingAssetsContext";

const SIDER_WIDTH = 240;

/** Convierte un Chapter en un item de Ant Menu.
 *  Si tiene sub-capítulos → SubMenu colapsable, con el propio capítulo
 *  como primera entrada ("Introducción") para no perder la portada. */
function chapterToMenuItem(
  c: Chapter,
): NonNullable<MenuProps["items"]>[number] {
  if (c.subChapters && c.subChapters.length > 0) {
    return {
      key: `cap-${c.slug}`,
      icon: getIcon(c.icon),
      label: c.label,
      children: [
        {
          key: `cap-${c.slug}-intro`,
          label: (
            <Link to={`/onboarding/capitulos/${c.slug}`}>Introducción</Link>
          ),
        },
        ...c.subChapters.map((sc) => ({
          key: `cap-${sc.slug}`,
          label: (
            <Link to={`/onboarding/capitulos/${sc.slug}`}>{sc.label}</Link>
          ),
        })),
      ],
    };
  }
  return {
    key: `cap-${c.slug}`,
    icon: getIcon(c.icon),
    label: <Link to={`/onboarding/capitulos/${c.slug}`}>{c.label}</Link>,
  };
}

export const OnboardingLayout: React.FC = () => {
  const { pathname } = useLocation();
  const { data: identity } = useGetIdentity<UserInfo>();
  const isAdmin = (ADMIN_USER_TYPES as readonly number[]).includes(
    identity?.app_user?.user_type ?? -1,
  );

  // Determinar la clave activa desde la URL. Cada ruta tiene una key.
  // Con sub-capítulos, la portada del padre (/onboarding/capitulos/X)
  // resuelve a `cap-X-intro`; los sub-capítulos a `cap-<sub-slug>`.
  const parentSlugBySub = new Map<string, string>();
  for (const c of chapters) {
    if (c.subChapters) {
      for (const sc of c.subChapters) {
        parentSlugBySub.set(sc.slug, c.slug);
      }
    }
  }
  const parentSlugsWithSubs = new Set(
    chapters.filter((c) => c.subChapters?.length).map((c) => c.slug),
  );

  const { selectedKey, openKey } = (() => {
    if (pathname === "/onboarding" || pathname === "/onboarding/") {
      return { selectedKey: "home", openKey: null as string | null };
    }
    if (pathname.startsWith("/onboarding/capitulos/")) {
      const slug =
        pathname.split("/onboarding/capitulos/")[1]?.split("/")[0] ?? "";
      // Si es un sub-capítulo, abrimos el submenú del padre.
      const parentSlug = parentSlugBySub.get(slug);
      if (parentSlug) {
        return {
          selectedKey: `cap-${slug}`,
          openKey: `cap-${parentSlug}`,
        };
      }
      // Si es la portada de un capítulo con sub-hijos, marcamos la
      // entrada "Introducción" dentro de su submenú.
      if (parentSlugsWithSubs.has(slug)) {
        return {
          selectedKey: `cap-${slug}-intro`,
          openKey: `cap-${slug}`,
        };
      }
      return { selectedKey: slug ? `cap-${slug}` : "home", openKey: null };
    }
    if (pathname.startsWith("/onboarding/documentos"))
      return { selectedKey: "documentos", openKey: null };
    if (pathname.startsWith("/onboarding/videos"))
      return { selectedKey: "videos", openKey: null };
    if (pathname.startsWith("/onboarding/faq"))
      return { selectedKey: "faq", openKey: null };
    if (pathname.startsWith("/onboarding/admin/assets"))
      return { selectedKey: "assets-admin", openKey: null };
    return { selectedKey: "home", openKey: null };
  })();

  const items: MenuProps["items"] = [
    {
      key: "home",
      icon: <HomeOutlined />,
      label: <Link to="/onboarding">Inicio</Link>,
    },
    {
      key: "capitulos",
      label: "Capítulos",
      type: "group",
      children: chapters.map(chapterToMenuItem),
    },
    { type: "divider" },
    {
      key: "documentos",
      icon: <FileTextOutlined />,
      label: <Link to="/onboarding/documentos">Documentos</Link>,
    },
    {
      key: "videos",
      icon: <VideoCameraOutlined />,
      label: <Link to="/onboarding/videos">Vídeo-tutoriales</Link>,
    },
    {
      key: "faq",
      icon: <QuestionCircleOutlined />,
      label: <Link to="/onboarding/faq">FAQ</Link>,
    },
    // Sub-sección admin — sólo visible para user_type 1/2. Vive
    // dentro del sub-sider del onboarding para que la gestión de
    // assets (subir PDFs / URLs de vídeo) esté al lado del propio
    // manual, no perdida bajo "Admin" en el sider global.
    ...(isAdmin
      ? [
          { type: "divider" as const },
          {
            key: "assets-admin",
            icon: <CloudUploadOutlined />,
            label: (
              <Link to="/onboarding/admin/assets">Gestión de assets</Link>
            ),
          },
        ]
      : []),
  ];

  // Submenús que deben estar abiertos por defecto: siempre el grupo
  // "capitulos" + el submenú del capítulo con hijos que corresponda a
  // la URL activa (para que el auditor no lo tenga que abrir a mano).
  const defaultOpenKeys = ["capitulos", ...(openKey ? [openKey] : [])];

  // Flex plano en vez de <Layout hasSider> anidado — evita que el
  // Layout AntD interno se pelee con el Layout externo del
  // ThemedLayout de Refine (los dos aplican flex-direction y
  // hasSider, y en algunos escenarios el sub-sider acaba
  // superponiéndose al sider global).
  //
  // `margin: -12px` neutraliza el padding del wrapper interno del
  // <Content> del ThemedLayout (Refine antd aplica padding: 12 en
  // desktop / 24 en móvil). Así el sub-sider queda pegado a la
  // derecha del sider global, sin gap.
  return (
    <OnboardingAssetsProvider>
    <div
      style={{
        display: "flex",
        flexDirection: "row",
        margin: -12,
        minHeight: "calc(100vh - 64px)",
        background: "#fff",
      }}
    >
      {/* `position: sticky` + `top: 64` mantiene el sub-sider fijo a
          la derecha del sider global (que tiene el header de 64px
          encima) cuando el auditor hace scroll en el contenido.
          `alignSelf: flex-start` es imprescindible para que sticky
          funcione dentro de un flex container.
          `height` (no `maxHeight`) para que el fondo del sider llene
          toda la altura visible aunque los items no lleguen abajo —
          si no, se ve un corte feo bajo el último item. `overflowY:
          auto` sigue permitiendo scroll interno si algún día el menú
          crece más que la pantalla. */}
      <aside
        style={{
          width: SIDER_WIDTH,
          flexShrink: 0,
          background: "#fafafa",
          borderRight: "1px solid #f0f0f0",
          position: "sticky",
          top: 64,
          alignSelf: "flex-start",
          height: "calc(100vh - 64px)",
          overflowY: "auto",
        }}
      >
        <Menu
          mode="inline"
          selectedKeys={[selectedKey]}
          defaultOpenKeys={defaultOpenKeys}
          items={items}
          style={{
            background: "transparent",
            borderRight: 0,
            paddingTop: 8,
          }}
        />
      </aside>
      <main
        style={{
          flex: 1,
          minWidth: 0,
          padding: "24px 32px",
          background: "#fff",
          overflowX: "auto",
        }}
      >
        <Outlet />
      </main>
    </div>
    </OnboardingAssetsProvider>
  );
};
